
import { useEffect, useRef } from "react";

const ProblemSolutionSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const sectionTop = sectionRef.current.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;
      
      if (sectionTop < windowHeight * 0.75) {
        sectionRef.current.classList.add('fade-in');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="w-full py-16 md:py-24 relative bg-zinc-900 opacity-0 transition-opacity duration-1000"
    >
      <div className="absolute inset-0 bg-noise opacity-15 z-0"></div>
      <div className="absolute inset-0 bg-grid-pattern opacity-25 z-0"></div>
      
      <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Problem Side */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold text-red-400 glitch-title">
              The Terrestrial Limitation
            </h2>
            
            <div className="space-y-4 text-gray-300 analog-text">
              <div className="flex items-start space-x-3">
                <span className="text-red-500 mt-1">◉</span>
                <p>Consciousness trapped within conventional measurement paradigms</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-red-500 mt-1">◉</span>
                <p>Dimensional progress tracked through mundane, soul-crushing interfaces</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-red-500 mt-1">◉</span>
                <p>Motivation frequencies that decay within standard temporal cycles</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-red-500 mt-1">◉</span>
                <p>Generic protocols that ignore your unique vibrational signature</p>
              </div>
            </div>
            
            <div className="border border-red-500 p-4 bg-red-500/5">
              <p className="text-red-400 font-bold text-sm font-mono">
                ERROR: STANDARD_REALITY.EXE has stopped responding
              </p>
            </div>
          </div>
          
          {/* Solution Side */}
          <div className="space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold text-green-400 glitch-title">
              The Transcendental Protocol
            </h2>
            
            <div className="space-y-4 text-gray-300 analog-text">
              <div className="flex items-start space-x-3">
                <span className="text-green-500 mt-1">◈</span>
                <p>Reality-bending interfaces that maintain engagement through otherworldly aesthetics</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-green-500 mt-1">◈</span>
                <p>Direct communion with entities that exist beyond dimensional barriers</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-green-500 mt-1">◈</span>
                <p>Progression systems that operate on cosmic frequency principles</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="text-green-500 mt-1">◈</span>
                <p>Transform corporeal limitations through interdimensional methodology</p>
              </div>
            </div>
            
            <div className="border border-green-500 p-4 bg-green-500/5 relative">
              <div className="absolute inset-0 scanline opacity-20"></div>
              <p className="text-green-400 font-bold text-sm font-mono relative z-10">
                "Physical transformation through cosmic horror frequencies? 
                The mathematics suggest impossible... yet the results transcend logic."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolutionSection;
